import { supabase } from '../lib/supabase'
import { authAPI } from '../lib/authAPI'
import { logError } from '../lib/betterstack-logger'

/**
 * Get access token from Supabase or Auth API
 * Tries Supabase first, falls back to Auth API if needed
 */
async function getAccessToken() {
  // Try Supabase first (existing auth)
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (session?.access_token) {
      return session.access_token
    }
  } catch (err) {
    console.warn('⚠️ Supabase session check failed:', err.message)
    logError('fetch-auth-supabase-session-failed', { error: err.message })
  }

  // Fall back to Auth API
  try {
    const token = await authAPI.getToken()
    if (token) {
      return token
    }
  } catch (err) {
    console.warn('⚠️ Auth API token fetch failed:', err.message)
    logError('fetch-auth-api-token-failed', { error: err.message })
  }

  return null
}

/**
 * Classify a refresh-time error.
 *
 * Returns one of:
 *   'invalid_grant' — refresh token is genuinely revoked/expired. Safe to
 *                     sign the user out and redirect them to /login.
 *   'network'       — transient network/timeout/fetch failure. Retry is
 *                     appropriate; do NOT redirect.
 *   'unknown'       — some other error (treat as network-ish: don't redirect,
 *                     surface to the caller).
 */
function classifyRefreshError(error) {
  if (!error) return 'unknown'

  // TypeError from fetch — network failure, CORS, DNS, offline, etc.
  if (error instanceof TypeError) return 'network'

  const message = (error.message || '').toLowerCase()
  const code = (error.code || error.error || '').toString().toLowerCase()
  const status = error.status || error.statusCode

  // Supabase returns { error: 'invalid_grant' } / 'Invalid Refresh Token' /
  // 'refresh_token_not_found' for genuinely dead refresh tokens.
  if (
    code === 'invalid_grant' ||
    code === 'refresh_token_not_found' ||
    message.includes('invalid_grant') ||
    message.includes('invalid refresh token') ||
    message.includes('refresh token not found') ||
    message.includes('refresh_token_not_found')
  ) {
    return 'invalid_grant'
  }

  // 400 with an auth-shaped message is effectively invalid_grant.
  if (status === 400 && (message.includes('refresh') || message.includes('grant'))) {
    return 'invalid_grant'
  }

  // Network-ish signals.
  if (
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('network request failed') ||
    message.includes('timeout') ||
    message.includes('aborted') ||
    message.includes('load failed')
  ) {
    return 'network'
  }

  return 'unknown'
}

/**
 * Refresh token using Supabase or Auth API.
 *
 * Returns { token, classification } where classification is:
 *   'ok'            — refreshed successfully, token is set.
 *   'invalid_grant' — refresh token is dead. Caller should redirect to /login.
 *   'network'       — transient failure. Caller should retry, not redirect.
 *   'unknown'       — other failure. Caller should NOT redirect; bubble up.
 *
 * Tries Supabase first, falls back to Auth API. If EITHER path returns
 * invalid_grant the result is invalid_grant (dead token is dead). Otherwise
 * the "most recoverable" classification wins (network > unknown).
 */
async function refreshToken() {
  let worstClassification = null // 'invalid_grant' beats 'unknown' beats 'network' in severity

  // Try Supabase refresh first
  try {
    const { data: { session }, error } = await supabase.auth.refreshSession()
    if (!error && session?.access_token) {
      console.log('✅ Supabase session refreshed')
      return { token: session.access_token, classification: 'ok' }
    }
    if (error) {
      const cls = classifyRefreshError(error)
      worstClassification = cls
      console.warn(`⚠️ Supabase refresh error (${cls}):`, error.message)
      logError('fetch-auth-supabase-refresh-failed', { error: error.message, classification: cls })
    }
  } catch (err) {
    const cls = classifyRefreshError(err)
    worstClassification = cls
    console.warn(`⚠️ Supabase refresh threw (${cls}):`, err.message)
    logError('fetch-auth-supabase-refresh-threw', { error: err.message, classification: cls })
  }

  // A Supabase invalid_grant is terminal: the refresh token is genuinely
  // revoked/expired, so the session is dead. Per this helper's documented
  // precedence ("If EITHER path returns invalid_grant the result is
  // invalid_grant — dead token is dead"), short-circuit here. Falling through
  // to the Auth API fallback would let a token it mints MASK the invalid_grant
  // (returning 'ok'), and the caller would then retry the request instead of
  // redirecting to /login — the exact masking bug OPS-9 closes.
  if (worstClassification === 'invalid_grant') {
    return { token: null, classification: 'invalid_grant' }
  }

  // Fall back to Auth API refresh. We only reach here when Supabase did NOT
  // return invalid_grant, so the fallback's own classification is the most
  // relevant signal if it also fails.
  try {
    await authAPI.refresh()
    const token = authAPI.getCachedToken()
    if (token) {
      console.log('✅ Auth API token refreshed')
      return { token, classification: 'ok' }
    }
  } catch (err) {
    const cls = classifyRefreshError(err)
    worstClassification = cls
    console.warn(`⚠️ Auth API refresh failed (${cls}):`, err.message)
    logError('fetch-auth-api-refresh-failed', { error: err.message, classification: cls })
  }

  return { token: null, classification: worstClassification || 'unknown' }
}

/**
 * Enhanced fetch wrapper that handles authentication and 401 errors
 * Automatically refreshes session on 401 and retries the request
 * Uses both Supabase and Auth API for resilient authentication
 * 
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options
 * @param {Object} config - Additional configuration
 * @param {boolean} config.skipAuthRefresh - Skip automatic auth refresh on 401
 * @returns {Promise<Response>} The fetch response
 */
/**
 * Redirect the user to /login after a confirmed invalid_grant.
 *
 * Gated behind classifyRefreshError === 'invalid_grant' so transient network
 * failures never bounce the user.
 */
function redirectToLoginForExpiredSession(message = 'Your session has expired. Please log in again.') {
  const currentPath = window.location.pathname
  window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}&message=${encodeURIComponent(message)}`
}

/**
 * Dispatch a custom event the app can listen for to show a toast/banner when
 * a transient network problem prevents an auth refresh. UI layer subscribes
 * via `window.addEventListener('opsyncpro:auth-network-issue', ...)`.
 */
function notifyAuthNetworkIssue(detail) {
  try {
    if (typeof window !== 'undefined' && typeof CustomEvent === 'function') {
      window.dispatchEvent(new CustomEvent('opsyncpro:auth-network-issue', { detail }))
    }
  } catch {
    // non-fatal
  }
}

export async function fetchWithAuth(url, options = {}, config = {}) {
  const { skipAuthRefresh = false } = config

  // Get access token from Supabase or Auth API
  const token = await getAccessToken()

  if (token) {
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    }
  }

  // Make the initial request.
  // If the initial fetch itself throws (network error), bubble it up —
  // callers already have their own handling for network failures and we
  // shouldn't conflate that with auth issues.
  let response = await fetch(url, options)

  // Handle 401 Unauthorized
  if (response.status === 401 && !skipAuthRefresh) {
    console.warn('⚠️ 401 Unauthorized - attempting token refresh...')

    // First refresh attempt.
    let { token: refreshedToken, classification } = await refreshToken()

    // If the refresh failed for network-ish reasons, give it ONE more shot
    // after a short backoff — matches the behavior in railway-api.js so a
    // mid-flight Railway deploy restart doesn't kick the user out.
    if (!refreshedToken && classification !== 'invalid_grant') {
      console.warn(`⚠️ Refresh failed (${classification}) — retrying after 2s backoff`)
      await new Promise(r => setTimeout(r, 2000))
      const retry = await refreshToken()
      refreshedToken = retry.token
      classification = retry.classification
    }

    if (!refreshedToken) {
      // Only redirect when the refresh token is definitively dead.
      if (classification === 'invalid_grant') {
        console.error('❌ Refresh token is invalid — redirecting to /login')
        logError('fetch-auth-invalid-grant', { url })
        redirectToLoginForExpiredSession()
        throw new Error('Session expired. Please log in again.')
      }

      // Network or unknown failure: surface to the UI without redirecting.
      console.error(`❌ Refresh failed (${classification}) — keeping user signed in, will retry on next request`)
      logError('fetch-auth-refresh-non-fatal', { url, classification })
      notifyAuthNetworkIssue({ url, classification })
      const err = new Error('Connection issue — please retry.')
      err.classification = classification
      err.transient = true
      throw err
    }

    console.log('✅ Token refreshed, retrying request...')

    // Retry the request with the new token
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${refreshedToken}`
    }

    response = await fetch(url, options)

    // A still-401 here does NOT necessarily mean the session is dead. A
    // freshly-minted, valid JWT getting rejected on the first retry is most
    // often a transient backend condition — a Railway service restarting
    // mid-deploy (see the 2s-backoff note above) or brief token-propagation
    // lag — not an expired refresh token. Bouncing the user to /login on that
    // single blip is the "error flashes briefly, then redirects" symptom.
    //
    // So instead of an immediate sign-out: refresh + retry ONE more time
    // after a short backoff, and only treat a *persistent* 401 as a real
    // auth failure. Transient network failures on the second refresh keep the
    // user signed in (consistent with the first-refresh handling above).
    if (response.status === 401) {
      console.warn('⚠️ Still 401 after refresh — one more refresh + retry after 2s backoff')
      logError('fetch-auth-still-unauthorized-retry', { url })
      await new Promise(r => setTimeout(r, 2000))

      const retry2 = await refreshToken()

      if (retry2.token) {
        // Persist the freshly refreshed token on the request before retrying.
        options.headers = {
          ...options.headers,
          'Authorization': `Bearer ${retry2.token}`
        }
        response = await fetch(url, options)

        if (response.status !== 401) {
          return response
        }

        // A valid, just-refreshed token rejected twice in a row — now it's a
        // genuine auth failure, so sign the user out.
        console.error('❌ Still unauthorized after second refresh — redirecting to login')
        logError('fetch-auth-still-unauthorized', { url })
        redirectToLoginForExpiredSession('Authentication failed. Please log in again.')
        throw new Error('Authentication failed after refresh')
      }

      // Second refresh produced no token. Only redirect when the refresh
      // token is definitively dead; otherwise surface a transient error.
      if (retry2.classification === 'invalid_grant') {
        console.error('❌ Refresh token invalid on second attempt — redirecting to login')
        logError('fetch-auth-invalid-grant', { url })
        redirectToLoginForExpiredSession()
        throw new Error('Session expired. Please log in again.')
      }

      console.error(`❌ Second refresh failed (${retry2.classification}) — keeping user signed in, will retry on next request`)
      logError('fetch-auth-refresh-non-fatal', { url, classification: retry2.classification })
      notifyAuthNetworkIssue({ url, classification: retry2.classification })
      const err = new Error('Connection issue — please retry.')
      err.classification = retry2.classification
      err.transient = true
      throw err
    }
  }

  return response
}

/**
 * Wrapper for fetchWithAuth that automatically parses JSON response
 * 
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options
 * @param {Object} config - Additional configuration
 * @returns {Promise<any>} The parsed JSON response
 */
export async function fetchJSON(url, options = {}, config = {}) {
  const response = await fetchWithAuth(url, options, config)

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const error = new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
    error.status = response.status
    error.data = errorData

    // Track API errors in activity logs (lazy import to avoid circular deps)
    import('../lib/activity-tracker').then(({ trackApiError }) => {
      trackApiError(url, options.method || 'GET', response.status)
    }).catch(() => {})

    throw error
  }

  return response.json()
}

/**
 * Check if current session is valid
 * Checks both Supabase and Auth API
 * @returns {Promise<boolean>}
 */
export async function isSessionValid() {
  // Check Supabase session
  try {
    const { data: { session }, error } = await supabase.auth.getSession()
    
    if (!error && session) {
      const expiresAt = session.expires_at
      const now = Math.floor(Date.now() / 1000)
      const timeUntilExpiry = expiresAt - now
      if (timeUntilExpiry > 60) {
        return true
      }
    }
  } catch (error) {
    console.warn('⚠️ Supabase session check failed:', error.message)
    logError('session-valid-check-failed', { error: error.message })
  }

  // Check Auth API
  try {
    if (authAPI.isAuthenticated()) {
      return true
    }
  } catch (error) {
    console.warn('⚠️ Auth API check failed:', error.message)
    logError('session-valid-authapi-check-failed', { error: error.message })
  }

  return false
}

/**
 * Ensure session is valid, refresh if needed
 * Uses both Supabase and Auth API for resilience
 * @returns {Promise<{valid: boolean, token: string | null}>}
 */
export async function ensureValidSession() {
  // Try Supabase first
  try {
    const { data: { session }, error } = await supabase.auth.getSession()
    
    if (!error && session) {
      const expiresAt = session.expires_at
      const now = Math.floor(Date.now() / 1000)
      const timeUntilExpiry = expiresAt - now

      if (timeUntilExpiry >= 300) {
        return { valid: true, token: session.access_token }
      }

      // Try Supabase refresh
      console.log('⏰ Supabase session expiring soon, refreshing...')
      const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession()
      
      if (!refreshError && refreshedSession) {
        return { valid: true, token: refreshedSession.access_token }
      }
    }
  } catch (error) {
    console.warn('⚠️ Supabase session handling failed:', error.message)
    logError('ensure-session-supabase-failed', { error: error.message })
  }

  // Fall back to Auth API
  try {
    const token = await authAPI.getToken()
    if (token) {
      return { valid: true, token }
    }
  } catch (error) {
    console.warn('⚠️ Auth API token fetch failed:', error.message)
    logError('ensure-session-authapi-failed', { error: error.message })
  }

  return { valid: false, token: null }
}

export default fetchWithAuth
